import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';
import { Typography } from 'app/components/ui';
import { useUpdateTag } from 'entities/tag';

interface FractionValueFieldsProps {
  tagId: string;
  documentSuperId: number;
  defaultValue: string;
  delay?: number;
  disabled?: boolean;
}

const splitFraction = (value: string): { numerator: string; denominator: string } => {
  const slash = value.indexOf('/');
  if (slash === -1) return { numerator: value, denominator: '' };
  return { numerator: value.slice(0, slash), denominator: value.slice(slash + 1) };
};

export const FractionValueFields = ({ tagId, documentSuperId, defaultValue, delay = 500, disabled = false }: FractionValueFieldsProps) => {
  const { t } = useTranslation('tag');

  const { mutate: updateTag } = useUpdateTag();
  const partsRef = useRef(splitFraction(defaultValue));
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (dirtyRef.current) {
      updateTag({ id: tagId, value: `${partsRef.current.numerator}/${partsRef.current.denominator}`, documentSuperId });
      dirtyRef.current = false;
    }
  }, [tagId, documentSuperId, updateTag]);

  const handleChange = useCallback(
    (part: 'numerator' | 'denominator') => (e: React.ChangeEvent<HTMLInputElement>) => {
      partsRef.current = { ...partsRef.current, [part]: e.target.value };
      dirtyRef.current = true;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  const initial = splitFraction(defaultValue);

  return (
    <>
      <div className="property-row">
        <Typography fontWeight="semibold" className="property-label">
          {t('fraction.numerator')}
        </Typography>
        {disabled ? (
          <Typography variant="body-7" fontWeight="light">
            {initial.numerator || '-'}
          </Typography>
        ) : (
          <input type="text" className="form-control form-control-sm" defaultValue={initial.numerator} onChange={handleChange('numerator')} onBlur={flush} />
        )}
      </div>
      <div className="property-row">
        <Typography fontWeight="semibold" className="property-label">
          {t('fraction.denominator')}
        </Typography>
        {disabled ? (
          <Typography variant="body-7" fontWeight="light">
            {initial.denominator || '-'}
          </Typography>
        ) : (
          <input
            type="text"
            className="form-control form-control-sm"
            defaultValue={initial.denominator}
            onChange={handleChange('denominator')}
            onBlur={flush}
          />
        )}
      </div>
    </>
  );
};
